import { isAbortOrCanceledError } from "@captions-web/utils/abort-signals";

export class ScopedError extends Error {
  name = "ScopedError";
  label: string;
  durationMs: number;
  metadata?: Record<string, unknown>;

  constructor(
    props: { label: string; durationMs: number; metadata?: Record<string, unknown> },
    cause?: unknown
  ) {
    const scopedMessage = `[scoped:${props.label}] ${getErrorMessage(cause)}`;
    super(scopedMessage, { cause });

    this.label = props.label;
    this.durationMs = props.durationMs;
    this.metadata =
      cause instanceof ScopedError ? { ...cause.metadata, ...props.metadata } : props.metadata;
    this.stack = cause instanceof Error ? cause.stack : this.stack;
  }
}

export class ScopedAbortError extends ScopedError {
  name = "ScopedAbortError";
}

function extractMessageFromObject(object: unknown) {
  const messageKeys = ["message", "error"];

  for (const key of messageKeys) {
    if (
      typeof object === "object" &&
      object !== null &&
      key in object &&
      typeof object[key as keyof typeof object] === "string"
    ) {
      return object[key as keyof typeof object];
    }
  }
}

const MAX_DEPTH_CHECK = 5;

function extractErrorFromObject(object: unknown, depth = 0) {
  // extra safety check to avoid going too deep into an object in a runaway loop
  if (depth > MAX_DEPTH_CHECK) {
    return;
  }

  if (object instanceof Error) {
    return object.message;
  }

  const messageInObject = extractMessageFromObject(object);

  if (messageInObject) {
    return messageInObject;
  }

  if (typeof object === "object" && object !== null) {
    if ("error" in object) {
      if (typeof object.error === "object" && object.error !== null) {
        return extractErrorFromObject(object.error, depth + 1);
      }

      if (typeof object.error === "string" || typeof object.error === "number") {
        return String(object.error);
      }
    }

    if ("data" in object && typeof object.data === "object" && object.data !== null) {
      return extractErrorFromObject(object.data, depth + 1);
    }
  }
}

export function getErrorMessage(error: unknown) {
  try {
    if (typeof error === "number" || typeof error === "boolean" || typeof error === "symbol") {
      return String(error);
    }

    if (typeof error === "string" && error.length > 0) {
      return error;
    }

    const message = extractErrorFromObject(error);

    if (message) {
      return message;
    }
  } catch {
    // do nothing
  }

  return "unknown error";
}

export function getErrorScopedLabel(error: unknown) {
  if (error instanceof ScopedError) {
    return error.label;
  }
}

interface ScopedOptions<M extends Record<string, unknown>> {
  getMetadata?: () => M;
}

export async function $scoped<R, M extends Record<string, unknown>>(
  label: string,
  fn: Promise<R> | (() => Promise<R>),
  options?: ScopedOptions<M>
) {
  const start = performance.now();

  const metadata = options?.getMetadata?.();

  try {
    const promiseValue = typeof fn === "function" ? fn() : fn;

    return await promiseValue;
  } catch (error) {
    const durationMs = Math.ceil(performance.now() - start);

    if (isAbortOrCanceledError(error)) {
      throw new ScopedAbortError({ label, durationMs, metadata }, error);
    }

    throw new ScopedError({ label, durationMs, metadata }, error);
  }
}

export type BaseScopedTaskEventParams = {
  label: string;
  status: "start" | "finish" | "error" | "cancel";
  durationMs?: number;
  errorMessage?: string;
};

interface ScopedTaskOptions<M extends Record<string, unknown>> {
  getMetadata: () => M;
  trackEvent: (params: BaseScopedTaskEventParams & M) => void;
}

// similar to $scoped, but with additional tracking of the task
export async function $scopedTask<R, M extends Record<string, unknown>>(
  label: string,
  fn: () => Promise<R>,
  options: ScopedTaskOptions<M>
) {
  const start = performance.now();

  const metadata = options?.getMetadata?.();

  try {
    options.trackEvent({ label, status: "start", ...metadata });

    const promiseValue = typeof fn === "function" ? fn() : fn;

    const result = await promiseValue;

    options.trackEvent({
      label,
      status: "finish",
      ...metadata,
      durationMs: Math.ceil(performance.now() - start),
    });

    return result;
  } catch (error) {
    const durationMs = Math.ceil(performance.now() - start);

    const errorScopedLabel = getErrorScopedLabel(error);
    const errorLabel = errorScopedLabel ? `${label} (${errorScopedLabel})` : label;

    if (isAbortOrCanceledError(error)) {
      options.trackEvent({ label: errorLabel, status: "cancel", durationMs, ...metadata });
      throw new ScopedAbortError({ label, durationMs, metadata }, error);
    }

    options.trackEvent({
      label: errorLabel,
      status: "error",
      durationMs,
      errorMessage: getErrorMessage(error),
      ...metadata,
    });

    throw new ScopedError({ label: errorLabel, durationMs, metadata }, error);
  }
}

export function $createBoundTask<M extends Record<string, unknown>>(options: ScopedTaskOptions<M>) {
  return <R>(label: string, fn: () => Promise<R>) => $scopedTask(label, fn, options);
}
